import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import React, { JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../library/formValidation';
import { useApiFormError } from '../../library/useApiFormError';
import { addPatientDefaults, AddPatientFormValues } from '../../library/models/clinic';
import ResourcesIds from '../../resources/ResourcesIds';
import { useIsCompact } from '../../library/useIsCompact';
import { closeIconStyles, descriptionStyles, dialogActionsStyles, dialogFormStyles, dialogTitleStyles, fieldsBoxStyles, formAlertStyles, namesBoxStyles } from './addPatientDialog.styles';
import { createAddPatientSchema } from './addPatientSchema';

interface AddPatientDialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** Rejects with the API error, which this dialog maps back onto its own fields. */
    onSubmit: (values: AddPatientFormValues) => Promise<unknown>;
}

/**
 * Adds a patient to the clinic without an evaluation.
 *
 * Lives here rather than beside either caller because it has two: the Patients page opens
 * it from its list, and the booking dialog from beside its patient picker. Both open it
 * empty.
 * One dialog is also what keeps its field caps answering to one `@Size` on the API.
 *
 * The address is the only required field -- it is where the invitation goes, and the
 * patient fills in the rest when they claim the account. Uses no router hooks, so it
 * renders on its own in a test.
 */
export const AddPatientDialog = (props: AddPatientDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();

    const schema = React.useMemo(() => createAddPatientSchema(translate), [translate]);

    const {
        register, handleSubmit, reset, setError, formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<AddPatientFormValues>(schema, {
        defaultValues: addPatientDefaults,
    });

    const knownFields: string[] = React.useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<AddPatientFormValues>(
        setError, knownFields, translate, ResourcesIds.ADD_PATIENT_ERROR);

    const { isOpen } = props;

    // Emptied on every open, so a previous patient's details are never sent again by
    // accident.
    useEffect(() => {
        if (isOpen) {
            reset(addPatientDefaults);
            clear();
        }
    }, [isOpen, reset, clear]);

    const onValid = async (values: AddPatientFormValues): Promise<void> => {
        clear();

        try {
            await props.onSubmit(values);
        } catch (error: unknown) {
            // Stays open with the error on the field. Closing is the parent's to do, and
            // only a success earns it.
            report(error);
        }
    };

    // A stray click beside the form must not throw away what was typed into it, so only
    // Escape, the close icon and Cancel dismiss it -- and none of them mid-request.
    const onDialogClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown'): void => {
        if (reason !== 'backdropClick' && !isSubmitting) {
            props.onClose();
        }
    };

    return (
        <Dialog open={props.isOpen} onClose={onDialogClose} maxWidth="sm" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.ADD_PATIENT_TITLE)}

                <IconButton
                    aria-label={translate(ResourcesIds.CLOSE_DIALOG)}
                    onClick={props.onClose}
                    disabled={isSubmitting}
                    sx={closeIconStyles}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    {formError && <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>}

                    <Typography variant="body2" sx={descriptionStyles}>
                        {translate(ResourcesIds.ADD_PATIENT_DESCRIPTION)}
                    </Typography>

                    <Box sx={fieldsBoxStyles}>
                        {/* Focused rather than the name: it is the field that has to be
                            filled in, and in the common case -- a picker searched by name
                            -- it is also the empty one. */}
                        <TextField
                            {...register('emailAddress')}
                            fullWidth
                            autoFocus
                            type="email"
                            label={translate(ResourcesIds.EMAIL_LABEL)}
                            {...fieldProps(errors, 'emailAddress')}
                        />

                        <Box sx={namesBoxStyles}>
                            <TextField
                                {...register('firstName')}
                                fullWidth
                                label={translate(ResourcesIds.FIRST_NAME_LABEL)}
                                {...fieldProps(errors, 'firstName')}
                            />

                            <TextField
                                {...register('lastName')}
                                fullWidth
                                label={translate(ResourcesIds.LAST_NAME_LABEL)}
                                {...fieldProps(errors, 'lastName')}
                            />
                        </Box>

                        <TextField
                            {...register('phoneNumber')}
                            fullWidth
                            type="tel"
                            label={translate(ResourcesIds.PHONE_LABEL)}
                            {...fieldProps(errors, 'phoneNumber')}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={dialogActionsStyles}>
                    <Button onClick={props.onClose} variant="outlined" disabled={isSubmitting}>
                        {translate(ResourcesIds.CANCEL_TEXT)}
                    </Button>
                    <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
                        {translate(ResourcesIds.ADD_PATIENT_SUBMIT)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
